import AuthController from '../services/AuthController.js';
import { guardedFetch } from '../core/navGeneration.js';

const name = version => version === 'refresh-v1' ? 'Refreshed experience' : 'Current experience';

/** Runtime selection is outside the working definition and never part of Save. */
export function mountExperienceControls(root, options, refresh) {
  const { installationGuid, manageExperience = false, onChanged } = options;
  const controls = root.querySelector('.pv-inspector');
  const section = document.createElement('div');
  section.className = 'pv-experience';
  section.innerHTML = `<label data-pv-version-label>Experience <select data-pv-version disabled aria-label="Experience"></select></label>
    <label data-pv-conversation-label hidden>Conversation <select data-pv-conversation aria-label="Conversation"></select></label>
    <p data-pv-runtime-status role="status"></p><button type="button" data-pv-retry hidden>Reload experience</button>
    ${manageExperience && installationGuid ? `<div data-pv-upgrade hidden>
      <details><summary>What changes and how to revert</summary><p>The refreshed experience changes layout, review editing, and submission recovery. Your saved settings and flows stay in place. Revert switches the experience while keeping your latest edits. Existing visitor tabs may keep the previous experience until they reload.</p></details>
      <label><input type="checkbox" data-pv-reviewed> I reviewed every conversation and its behavior in the selected experience.</label>
      <p data-pv-change-note></p><button type="button" data-pv-apply disabled>Change experience</button>
    </div>` : ''}`;
  controls.prepend(section);
  section.querySelector('[data-pv-version-label]').hidden = !manageExperience;
  const select = section.querySelector('[data-pv-version]');
  const status = section.querySelector('[data-pv-runtime-status]');
  const retry = section.querySelector('[data-pv-retry]');
  const conv = section.querySelector('[data-pv-conversation]');
  const apply = section.querySelector('[data-pv-apply]');
  const reviewed = section.querySelector('[data-pv-reviewed]');
  const changeNote = section.querySelector('[data-pv-change-note]');
  const frame = root.querySelector('iframe');
  const state = root.querySelector('[data-pv-state]');
  const endpoint = `${AuthController.getConfig?.().BASE_URL || ''}/m5t/v5/magnets/${encodeURIComponent(installationGuid || '')}/experience`;
  let review = null, disposed = false, generation = 0, busy = false;
  const abort = new AbortController();

  function update() {
    if (!review) return;
    if (!manageExperience) select.value = review.effective;
    const runtime = review.runtimes[select.value];
    const serving = select.value === review.effective;
    status.textContent = (options.progressive && serving ? 'Using this magnet’s selected experience. ' : `${name(select.value)} · ${serving ? 'selected for this installation' : 'comparison only'}. `)
      + (review.assigned !== review.effective ? 'The refreshed assignment is currently paused by the rollout controls. ' : '')
      + (runtime.pinned ? '' : 'The current runtime is not yet pinned to a release. ')
      + (select.value === 'current' ? 'Preview your actual flow; sample states are available in the refreshed experience. ' : '')
      + (review.preview_unavailable || '');
    status.hidden = !manageExperience && serving && review.assigned === review.effective && runtime.pinned && !review.preview_unavailable;
    status.title = `Release SHA-256: ${runtime.sha256}`;
    state.disabled = select.value === 'current';
    if (state.disabled) state.value = 'flow';
    if (apply) {
      const reason = !review.can_manage ? 'You can preview this installation but cannot change its experience.'
        : select.value === review.assigned ? 'This experience is already assigned.'
          : select.value === 'refresh-v1' ? review.upgrade_blocked : null;
      changeNote.textContent = reason || `Applies to all ${review.conversations.length} conversation${review.conversations.length === 1 ? '' : 's'} on this installation. Cached embeds may take up to five minutes to update.`;
      apply.textContent = select.value === 'refresh-v1' ? 'Upgrade to refreshed experience' : 'Revert to current experience';
      apply.disabled = busy || !!reason || !reviewed.checked;
      reviewed.disabled = busy || !!reason;
    }
  }
  async function load() {
    const request = ++generation;
    review = null; select.disabled = true; retry.hidden = true;
    frame.hidden = true;
    // Do not start an empty srcdoc navigation on a newly mounted frame. WebKit
    // can let that first navigation win over the immediately resolved API render.
    if (frame.hasAttribute('srcdoc')) frame.srcdoc = '';
    status.hidden = false;
    status.textContent = manageExperience ? 'Loading the published experience…' : 'Loading preview…';
    if (apply) { apply.disabled = true; reviewed.checked = false; }
    try {
      const response = await guardedFetch(endpoint, { cache: 'no-store', signal: abort.signal });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || 'The published experience could not be loaded.');
      if (!data.runtimes?.[data.effective]?.source || !data.revision || !Array.isArray(data.conversations)) {
        throw new Error('The server must support version-aware previews. Reload after the API update.');
      }
      if (disposed || request !== generation) return;
      review = data;
      select.replaceChildren(...['current', 'refresh-v1'].filter(v => data.runtimes[v]).map(v => {
        const option = document.createElement('option'); option.value = v; option.textContent = name(v); return option;
      }));
      select.value = data.effective; select.disabled = false;
      conv.replaceChildren(...data.conversations.map(d => {
        const option = document.createElement('option'); option.value = d.guid;
        option.textContent = `${d.definition?.mag_title || 'Untitled conversation'} (${d.status.toLowerCase()})`; return option;
      }));
      section.querySelector('[data-pv-conversation-label]').hidden = !manageExperience || data.conversations.length < 2;
      if (apply) section.querySelector('[data-pv-upgrade]').hidden = false;
      frame.hidden = false; update(); refresh();
    } catch (error) {
      if (disposed || request !== generation) return;
      status.hidden = false;
      status.textContent = error.message || 'The published experience could not be loaded.';
      retry.hidden = false;
    }
  }
  select.addEventListener('change', () => { if (reviewed) reviewed.checked = false; update(); });
  reviewed?.addEventListener('change', update);
  retry.addEventListener('click', load);
  apply?.addEventListener('click', async () => {
    if (apply.disabled || !review || busy) return;
    busy = true; update(); select.disabled = true; retry.hidden = true;
    const target = select.value;
    try {
      const response = await guardedFetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ save_version: 1, expected_revision: review.revision,
          expected_installation_revision: review.installation_revision, experience: target,
          expected_artifact_sha256: review.runtimes[target].sha256 }) });
      const result = await response.json();
      if (!response.ok || !result.ok || !result.revision) throw new Error(result.error || 'The result could not be confirmed. Reload the experience before trying again.');
      if (disposed) return;
      await load();
      if (!disposed) onChanged?.(result);
    } catch (error) {
      if (disposed) return;
      // Never retry an uncertain mutation with a new revision automatically.
      review = null; frame.hidden = true; frame.srcdoc = '';
      status.textContent = `${error.message} Reload to check the saved assignment before trying again.`;
      retry.hidden = false;
    } finally { busy = false; if (!disposed) update(); }
  });

  if (installationGuid) void load();
  else {
    section.querySelector('[data-pv-version-label]').hidden = true;
    status.textContent = 'Refreshed design preview · not a published experience. New magnets keep the current experience until explicitly upgraded.';
  }
  return {
    get(pkg) {
      if (!installationGuid) return { pkg };
      if (!review) return null; // Never substitute a local bundle after an API failure.
      update();
      return { pkg: manageExperience ? (review.conversations.find(d => d.guid === conv.value)?.definition || review.definition) : pkg,
        sdk: review.runtimes[select.value].source };
    },
    destroy() { disposed = true; generation++; abort.abort(); },
  };
}
